/**
 * InAppBrowserBanner — Detects when the page is loaded inside an in-app
 * browser (TikTok, Instagram, Facebook, Snapchat) and renders a FULL-SCREEN
 * blocker until the visitor opens the page in their default browser.
 *
 * WHY FULL-SCREEN AND NOT DISMISSABLE:
 *   IABs silently break Stripe Payment Element (Apple Pay / Google Pay
 *   buttons don't render, card focus fails, PaymentIntent confirmation
 *   hangs). Letting the visitor proceed is letting them waste their time.
 *   Better to block aggressively + give them a one-tap escape path.
 *
 * TECHNICAL ESCAPE HATCHES:
 *   - Android: `intent://` URL forces the link open in Chrome's full UI.
 *     This bypasses TikTok IAB completely on ~70% of devices.
 *   - iOS: there is NO supported URL scheme to force Safari from inside an
 *     IAB. Best we can do is copy the URL to clipboard + show step-by-step
 *     instructions ("tap ⋯ then Open in Safari").
 *
 * NOTE: the "real" fix is the user setting "Open Externally" on their
 *   TikTok ad — that bypasses the IAB at the ad-click stage. This banner
 *   is the defensive safety net for visitors who slip through.
 */

import { useEffect, useState } from "react";
import { AlertTriangle, ExternalLink, Copy, Check, ChevronRight } from "lucide-react";

const IAB_PATTERNS = [
  /TikTok/i, /Bytedance/i, /musical_ly/i,
  /Instagram/i, /FBAV/i, /FBAN/i, /FB_IAB/i,
  /Snapchat/i, /LinkedInApp/i, /Twitter/i,
];

const detectIAB = () => {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  for (const re of IAB_PATTERNS) {
    if (re.test(ua)) {
      const isIOS = /iPhone|iPad|iPod/i.test(ua);
      const isAndroid = /Android/i.test(ua);
      const name = re.toString().includes("TikTok") || re.toString().includes("musical")
        ? "TikTok"
        : re.toString().includes("Instagram")
        ? "Instagram"
        : re.toString().includes("FB")
        ? "Facebook"
        : re.toString().includes("Snapchat")
        ? "Snapchat"
        : "in-app browser";
      return { name, isIOS, isAndroid, ua };
    }
  }
  return null;
};

const InAppBrowserBanner = () => {
  const [iab, setIab] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setIab(detectIAB());
  }, []);

  if (!iab) return null;

  const fullUrl = typeof window !== "undefined" ? window.location.href : "";

  // Android: build an `intent://` URL that forces the link to open in
  // Chrome's full browser UI. Some IABs may still intercept but this is the
  // single highest-leverage Android escape hatch.
  const buildChromeIntent = () => {
    if (typeof window === "undefined") return "#";
    const url = window.location.href.replace(/^https?:\/\//, "");
    return `intent://${url}#Intent;scheme=https;package=com.android.chrome;end`;
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      // Fallback for non-clipboard browsers — create a hidden textarea
      try {
        const ta = document.createElement("textarea");
        ta.value = fullUrl;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      } catch (_) { /* nothing else we can do */ }
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-[#0A0410] text-[#F6EFE7] overflow-y-auto"
      data-testid="iab-fullscreen-blocker"
      dir="ltr"
    >
      <div className="min-h-full flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-md">
          {/* Logo + warning icon */}
          <div className="flex justify-center mb-5">
            <div className="w-16 h-16 rounded-full bg-[#7C1F1F] flex items-center justify-center border-2 border-[#F87171]/40">
              <AlertTriangle className="w-8 h-8 text-[#FFB4B4]" />
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-center text-[20px] font-extrabold mb-2 text-white">
            Open in {iab.isIOS ? "Safari" : "Chrome"} to Continue
          </h1>
          <p className="text-center text-[13px] text-[#A795B8] mb-3 leading-relaxed">
            Payment doesn&apos;t work inside the <span className="font-bold text-white">{iab.name}</span> in-app browser.
            Open this page in your phone&apos;s default browser to complete checkout.
          </p>

          {/* Arabic equivalent — funnel-agnostic so this component works on
              both /7days (Arabic) and /reply (English). */}
          <p className="text-center text-[13px] text-[#A795B8] mb-6 leading-[1.85]" dir="rtl">
            صفحة الدفع لا تعمل داخل تطبيق {iab.name}.
            <br />
            الرجاء فتح الرابط في متصفّحك الأساسي لإتمام الشراء.
          </p>

          {/* Android: one-tap Chrome intent button */}
          {iab.isAndroid && (
            <a
              href={buildChromeIntent()}
              className="block w-full bg-[#E8A18F] hover:bg-[#F2BBA8] text-[#1A0E22] font-extrabold text-[15px] py-4 px-5 rounded-2xl text-center mb-3 active:scale-[0.98] transition-transform"
              data-testid="iab-open-chrome-btn"
            >
              <ExternalLink className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              Open in Chrome
            </a>
          )}

          {/* iOS: copy link + step-by-step Safari instructions */}
          {iab.isIOS && (
            <>
              <button
                onClick={onCopy}
                className="block w-full bg-[#E8A18F] hover:bg-[#F2BBA8] text-[#1A0E22] font-extrabold text-[15px] py-4 px-5 rounded-2xl text-center mb-3 active:scale-[0.98] transition-transform"
                data-testid="iab-copy-link-btn"
              >
                {copied ? (
                  <><Check className="w-4 h-4 inline-block mr-2 -mt-0.5" /> Link Copied!</>
                ) : (
                  <><Copy className="w-4 h-4 inline-block mr-2 -mt-0.5" /> Copy Link</>
                )}
              </button>

              <div className="bg-[#1A0E22]/60 border border-[#D9707F]/25 rounded-2xl p-4 mb-3 text-[12.5px] text-[#F6EFE7] space-y-2">
                <div className="font-bold text-[#E8A18F] mb-2">Next steps:</div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-[#A795B8] shrink-0 mt-0.5" />
                  <span>Tap the <span className="font-bold">⋯</span> icon at the top-right of {iab.name}</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-[#A795B8] shrink-0 mt-0.5" />
                  <span>Choose <span className="font-bold">&quot;Open in Safari&quot;</span> (or &quot;Open in Browser&quot;)</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-[#A795B8] shrink-0 mt-0.5" />
                  <span>If that option isn&apos;t there, paste the copied link in Safari directly</span>
                </div>
              </div>
            </>
          )}

          {/* Fallback for desktop IAB (rare) */}
          {!iab.isIOS && !iab.isAndroid && (
            <button
              onClick={onCopy}
              className="block w-full bg-[#E8A18F] hover:bg-[#F2BBA8] text-[#1A0E22] font-extrabold text-[15px] py-4 px-5 rounded-2xl text-center mb-3"
              data-testid="iab-copy-link-btn"
            >
              {copied ? <><Check className="w-4 h-4 inline-block mr-2" /> Copied!</> : <><Copy className="w-4 h-4 inline-block mr-2" /> Copy Link</>}
            </button>
          )}

          {/* URL preview */}
          <div className="text-center text-[11px] text-[#A795B8] mt-4 break-all" data-testid="iab-current-url">
            {fullUrl}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InAppBrowserBanner;

